import type { Region } from "@/data/regions";
import { regions } from "@/data/regions";
import type { Service } from "@/data/services";

/** Kernzoekwoorden per dienst, in de bewoording die klanten in Google typen. */
const serviceTerms: Record<string, string[]> = {
  "particuliere-verhuizing": [
    "verhuisbedrijf",
    "verhuizers",
    "verhuizen",
    "goedkoop verhuizen",
    "woning verhuizen",
    "verhuizer",
  ],
  "zakelijke-verhuizing": ["zakelijke verhuizing", "kantoorverhuizing", "bedrijfsverhuizing"],
  seniorenverhuizing: ["seniorenverhuizing", "verhuishulp senioren", "spoedverhuizing senioren"],
  ontruiming: ["woningontruiming", "bedrijfsontruiming", "huis leegruimen"],
  opslag: [
    "opslag",
    "inboedelopslag",
    "tijdelijke opslag",
    "opslag meubels",
    "opslag verhuizing",
    "verhuizen en opslag",
    "opslagruimte huren",
  ],
  transport: ["transport", "meubeltransport", "pianotransport"],
  evenementen: ["evenementenlogistiek", "op- en afbouw evenement"],
  inpakservice: ["inpakservice", "demontage en montage", "inpakhulp"],
  verhuislift: ["verhuislift huren", "verhuislift met bediening", "verhuislift"],
};

const genericTerms = [
  "verhuisbedrijf",
  "verhuizers",
  "verhuizen",
  "verhuizing",
  "goedkoop verhuizen",
  "verhuislift huren",
  "inpakservice",
  "opslag",
  "inboedelopslag",
  "tijdelijke opslag",
  "woningontruiming",
  "zakelijke verhuizing",
  "verhuizen prijs",
  "verhuisbedrijf offerte",
  "opslagruimte huren",
  "kantoorverhuizing",
  "bedrijfsverhuizing",
  "seniorenverhuizing",
  "spoedverhuizing",
  "piano verhuizen",
  "meubeltransport",
  "verhuizen prijs",
  "wat kost een verhuizing",
];


/** Lokale zoekwoorden voor een werkgebiedpagina: dienst + plaats + wijken. */
export function regionKeywords(region: Region): string[] {
  const city = region.city;
  const combos = genericTerms.flatMap((term) => [`${term} ${city}`, `${term} in ${city}`]);
  const services = Object.values(serviceTerms)
    .map((terms) => terms[0])
    .map((term) => `${term} ${city}`);
  const hoods = region.neighbourhoods.slice(0, 6).map((n) => `verhuizer ${n} ${city}`);
  const nearby = region.nearby
    .map((slug) => regions.find((r) => r.slug === slug)?.city)
    .filter((c): c is string => Boolean(c))
    .map((c) => `verhuisbedrijf ${c}`);

  return [
    `verhuisbedrijf ${city}`,
    `verhuizers ${city}`,
    `verhuisbedrijf ${region.group}`,
    ...combos,
    ...services,
    ...hoods,
    ...nearby,
    "verhuisbedrijf Zuid-Holland",
    "verhuisbedrijf Nederland",
  ];
}

/** Belangrijkste plaatsen voor dienstpagina's: één sterke stad per regiogroep, plus de rest van Haaglanden. */
const primaryCities = [
  "Den Haag",
  "Rijswijk",
  "Delft",
  "Zoetermeer",
  "Leidschendam-Voorburg",
  "Wassenaar",
  "Rotterdam",
  "Schiedam",
  "Leiden",
  "Gouda",
  "Dordrecht",
  "Alphen aan den Rijn",
];

/** Lokale zoekwoorden voor een dienstpagina: dienst + de belangrijkste plaatsen. */
export function serviceKeywords(service: Service): string[] {
  const terms = serviceTerms[service.slug] ?? [service.title.toLowerCase()];
  const combos = terms.flatMap((term) => primaryCities.map((city) => `${term} ${city}`));
  return [
    service.title.toLowerCase(),
    ...terms,
    ...combos,
    `${terms[0]} Zuid-Holland`,
    `${terms[0]} Nederland`,
  ];
}

/** Zoekwoorden voor overzichts- en homepagina's. */
export function overviewKeywords(extra: string[] = []): string[] {
  const cityCombos = primaryCities.flatMap((city) => [
    `verhuisbedrijf ${city}`,
    `verhuizers ${city}`,
  ]);
  return [...genericTerms, ...cityCombos, "verhuisbedrijf Zuid-Holland", ...extra];
}
